import type { ReactElement, ReactNode } from 'react';

/*
 * The App Store's drawn app icons: one per app, shown in the tile's mark box in
 * place of the two-letter monogram. An app with no drawing keeps its monogram, so
 * a new app never needs one to appear. Tools keep their monograms.
 *
 * Every shape is drawn on a 48-unit grid and colored only through the classes
 * below (launcher.css): the category color from the tile's --gp-fd-accent, the
 * outline from --gp-fd-ink, and "paper" from the tile's background. No fixed
 * colors, so dark mode restyles the drawing with the rest of the tile
 * (appIcons.test.tsx enforces it).
 */

/** A shape filled with the category color, outlined in ink. */
const FILL = 'gp-fd-icon-fill';
/** A shape filled with the tile background, outlined in ink. */
const PAPER = 'gp-fd-icon-paper';
/** An ink line. */
const LINE = 'gp-fd-icon-line';
/** A solid ink shape. */
const INK = 'gp-fd-icon-ink';
/** A solid category-color shape with no outline. */
const TINT = 'gp-fd-icon-tint';
/** A solid tile-background shape with no outline: a hole punched in ink. */
const HOLE = 'gp-fd-icon-hole';

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg className="gp-fd-mark-icon" viewBox="2 2 44 44" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** Drawings by app id. Add one here when an app joins the App Store. */
export const APP_ICONS: Readonly<Record<string, ReactElement>> = {
  // A megaphone: membership news and announcements for the AGN community.
  'agn-portal': (
    <Icon>
      <rect className={PAPER} x="8" y="19" width="6" height="10" rx="1" />
      <path className={FILL} d="M14 19l17-7v24l-17-7z" />
      <path className={FILL} d="M16 29.5l2 7.5h4.5l-1.5-6.2z" />
      <path className={LINE} d="M35 19.5c1.8 2.9 1.8 6.1 0 9M38.5 16c3.4 5.1 3.4 10.9 0 16" />
    </Icon>
  ),
  // A game board with a pawn: deciding where to play.
  'alliance-gameboard': (
    <Icon>
      <rect className={PAPER} x="7" y="17" width="24" height="24" rx="1" />
      <path className={TINT} d="M7 17h8v8H7zM23 17h8v8h-8zM15 25h8v8h-8zM7 33h8v8H7zM23 33h8v8h-8z" />
      <rect className={LINE} x="7" y="17" width="24" height="24" rx="1" />
      <path className={FILL} d="M32.5 19h5l2.5 15h-10z" />
      <circle className={FILL} cx="35" cy="13.5" r="4" />
      <rect className={FILL} x="29" y="34" width="12" height="5" rx="1" />
    </Icon>
  ),
  // An open playbook with a sprout: an emerging relationship growing into an alliance.
  'er-playbook': (
    <Icon>
      <path className={FILL} d="M8 20c6-2 11-1 16 3v15c-5-4-10-5-16-3z" />
      <path className={FILL} d="M40 20c-6-2-11-1-16 3v15c5-4 10-5 16-3z" />
      <path className={FILL} d="M24 17c-4 0-6.5-3-6.5-6.5 4 0 6.5 3 6.5 6.5z" />
      <path className={FILL} d="M24 15c0-4 2.5-6.5 6.5-6.5 0 4-2.5 6.5-6.5 6.5z" />
      <path className={LINE} d="M24 23v-8" />
    </Icon>
  ),
  // Stage chevrons, two done and one next: where each formation stands.
  'formations-dashboard': (
    <Icon>
      <path className={FILL} d="M5 15h8l6 9-6 9H5l6-9z" />
      <path className={FILL} d="M16 15h8l6 9-6 9h-8l6-9z" />
      <path className={PAPER} d="M27 15h8l6 9-6 9h-8l6-9z" />
    </Icon>
  ),
  // A coin moving fast, with an up arrow: funding, accelerated.
  'funding-accelerator': (
    <Icon>
      <circle className={FILL} cx="28" cy="24" r="12" />
      <path className={LINE} d="M28 30v-11M23.5 23.5L28 19l4.5 4.5" />
      <path className={LINE} d="M7 18h6M5 24h8M7 30h6" />
    </Icon>
  ),
  // A hub and its spokes: which alliances connect to which accounts.
  'gp-infohub': (
    <Icon>
      <path className={LINE} d="M24 24V11M24 24l12-5M24 24l8.5 12M24 24l-8.5 12M24 24l-12-5" />
      <circle className={FILL} cx="24" cy="11" r="3.8" />
      <circle className={FILL} cx="36" cy="19" r="3.8" />
      <circle className={FILL} cx="32.5" cy="36" r="3.8" />
      <circle className={FILL} cx="15.5" cy="36" r="3.8" />
      <circle className={FILL} cx="12" cy="19" r="3.8" />
      <circle className={INK} cx="24" cy="24" r="5.5" />
      <circle className={HOLE} cx="24" cy="24" r="2" />
    </Icon>
  ),
  // Rising bars with a trend line: self-service reporting.
  'gp-reporting-demo': (
    <Icon>
      <rect className={FILL} x="9" y="27" width="8" height="12" rx="1" />
      <rect className={FILL} x="20" y="21" width="8" height="18" rx="1" />
      <rect className={FILL} x="31" y="15" width="8" height="24" rx="1" />
      <path className={LINE} d="M10 20l9-6 7 3 9-6.5" />
      <circle className={PAPER} cx="36.5" cy="10" r="2.6" />
    </Icon>
  ),
  // A compass: the Strategy team's bearings.
  'gp-strategy-os': (
    <Icon>
      <circle className={FILL} cx="24" cy="24" r="16" />
      <circle className={PAPER} cx="24" cy="24" r="12" />
      <path className={FILL} d="M30.7 17.3l-4.6 8.8-4.2-4.2z" />
      <path className={INK} d="M17.3 30.7l4.6-8.8 4.2 4.2z" />
      <circle className={HOLE} cx="24" cy="24" r="1.4" />
    </Icon>
  ),
  // An agenda on a clipboard, its first item starred.
  'leadership-agendas': (
    <Icon>
      <rect className={FILL} x="12" y="10" width="24" height="29" rx="2" />
      <rect className={INK} x="18.5" y="7" width="11" height="6" rx="1" />
      <path className={LINE} d="M22 21h9M22 27.5h9M22 34h5" />
      <path className={INK} d="M17 18.5l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2-1.6-1.5 2.2-.3z" />
      <circle className={INK} cx="17" cy="27.5" r="1.6" />
      <circle className={INK} cx="17" cy="34" r="1.6" />
    </Icon>
  ),
  // Kanban columns of cards, with work checked off.
  'pm-kanban': (
    <Icon>
      <rect className={FILL} x="8" y="10" width="9.5" height="7" rx="1" />
      <rect className={FILL} x="8" y="20" width="9.5" height="7" rx="1" />
      <rect className={FILL} x="8" y="30" width="9.5" height="7" rx="1" />
      <rect className={FILL} x="19.25" y="10" width="9.5" height="7" rx="1" />
      <rect className={FILL} x="19.25" y="20" width="9.5" height="7" rx="1" />
      <rect className={FILL} x="30.5" y="10" width="9.5" height="7" rx="1" />
      <path className={LINE} d="M32 33l3 3 5-6" />
    </Icon>
  ),
  // A warehouse with its roll-up door: the WorkSpan data, stored and refreshed.
  'workspan-warehouse': (
    <Icon>
      <path className={FILL} d="M7 21L24 10l17 11v18H7z" />
      <rect className={PAPER} x="15" y="25" width="18" height="14" />
      <path className={LINE} d="M15 29.7h18M15 34.3h18" />
    </Icon>
  ),
};

/** The drawn icon for an app, or undefined when it has none (it keeps its monogram). */
export function appIcon(appId: string): ReactElement | undefined {
  return Object.hasOwn(APP_ICONS, appId) ? APP_ICONS[appId] : undefined;
}
